import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  getDocs,
  collection,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import type { DailyLog, DailyCheckInData, DailyMetrics } from '../types';

// ─── Path Helpers ────────────────────────────────────────────────────────────

const logDoc = (userId: string, date: string) =>
  doc(getFirebaseDb(), 'users', userId, 'dailyLog', date);

const logsCollection = (userId: string) =>
  collection(getFirebaseDb(), 'users', userId, 'dailyLog');

// ─── Format date as YYYY-MM-DD ───────────────────────────────────────────────

export function toDateKey(date: Date = new Date()): string {
  return date.toISOString().split('T')[0];
}

// ─── Daily Log ───────────────────────────────────────────────────────────────

export async function getDailyLog(userId: string, date: string): Promise<DailyLog | null> {
  const snap = await getDoc(logDoc(userId, date));
  if (!snap.exists()) return null;
  return snap.data() as DailyLog;
}

export async function getRecentLogs(userId: string, days = 7): Promise<DailyLog[]> {
  const q = query(logsCollection(userId), orderBy('date', 'desc'), limit(days));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data() as DailyLog);
}

async function ensureLog(userId: string, date: string): Promise<void> {
  const snap = await getDoc(logDoc(userId, date));
  if (!snap.exists()) {
    const emptyLog: DailyLog = {
      date,
      userId,
      metrics: {
        date,
        sessionsCompleted: 0,
        nodesAdded: 0,
        edgesAdded: 0,
      },
    };
    await setDoc(logDoc(userId, date), emptyLog);
  }
}

export async function saveMorningCheckIn(
  userId: string,
  date: string,
  data: DailyCheckInData
): Promise<void> {
  await ensureLog(userId, date);
  await updateDoc(logDoc(userId, date), {
    morning: data,
    'metrics.morningMood': data.mood,
    'metrics.morningEnergy': data.energy,
  });
}

export async function saveEveningCheckIn(
  userId: string,
  date: string,
  data: DailyCheckInData
): Promise<void> {
  await ensureLog(userId, date);
  await updateDoc(logDoc(userId, date), {
    evening: data,
    'metrics.eveningMood': data.mood,
    'metrics.eveningEnergy': data.energy,
  });
}

interface SaveCheckInSignalsInput {
  sessionId: string;
  completedAt: DailyCheckInData['completedAt'];
  mood?: number;
  energy?: number;
}

export async function saveCheckInSignals(
  userId: string,
  date: string,
  sessionType: 'morning_checkin' | 'evening_checkin',
  input: SaveCheckInSignalsInput
): Promise<void> {
  await ensureLog(userId, date);
  const updates: Record<string, unknown> = {};
  const bucket = sessionType === 'morning_checkin' ? 'morning' : 'evening';
  updates[`${bucket}.sessionId`] = input.sessionId;
  updates[`${bucket}.completedAt`] = input.completedAt;

  if (typeof input.mood === 'number') {
    updates[`${bucket}.mood`] = input.mood;
    updates[`metrics.${bucket === 'morning' ? 'morningMood' : 'eveningMood'}`] = input.mood;
  }
  if (typeof input.energy === 'number') {
    updates[`${bucket}.energy`] = input.energy;
    updates[`metrics.${bucket === 'morning' ? 'morningEnergy' : 'eveningEnergy'}`] = input.energy;
  }

  await updateDoc(logDoc(userId, date), updates);
}

export async function incrementSessionsCompleted(userId: string, date: string): Promise<void> {
  await ensureLog(userId, date);
  const snap = await getDoc(logDoc(userId, date));
  const current = (snap.data()?.metrics?.sessionsCompleted as number) ?? 0;
  await updateDoc(logDoc(userId, date), {
    'metrics.sessionsCompleted': current + 1,
  });
}

export async function updateDailyGraphMetrics(
  userId: string,
  date: string,
  nodesAdded: number,
  edgesAdded: number
): Promise<void> {
  await ensureLog(userId, date);
  const snap = await getDoc(logDoc(userId, date));
  const metrics = snap.data()?.metrics as DailyMetrics | undefined;
  await updateDoc(logDoc(userId, date), {
    'metrics.nodesAdded': (metrics?.nodesAdded ?? 0) + nodesAdded,
    'metrics.edgesAdded': (metrics?.edgesAdded ?? 0) + edgesAdded,
  });
}

export async function savePersonalNote(
  userId: string,
  date: string,
  personalNote: string
): Promise<void> {
  await ensureLog(userId, date);
  await updateDoc(logDoc(userId, date), {
    personalNote,
  });
}

/** Returns the last N days of mood/energy as a simple time series. */
export async function getMoodEnergySeries(
  userId: string,
  days = 7
): Promise<Array<{ date: string; morningMood?: number; morningEnergy?: number; eveningMood?: number; eveningEnergy?: number }>> {
  const logs = await getRecentLogs(userId, days);
  return logs.map(l => ({
    date: l.date,
    morningMood: l.metrics.morningMood,
    morningEnergy: l.metrics.morningEnergy,
    eveningMood: l.metrics.eveningMood,
    eveningEnergy: l.metrics.eveningEnergy,
  }));
}

export async function getPreviousDayMoodEnergyDefaults(
  userId: string,
  referenceDate: Date = new Date()
): Promise<{ mood: number; energy: number }> {
  const previousDay = new Date(referenceDate);
  previousDay.setDate(previousDay.getDate() - 1);
  const log = await getDailyLog(userId, toDateKey(previousDay));

  const mood = clampMoodEnergy(log?.metrics?.eveningMood ?? log?.metrics?.morningMood ?? 5);
  const energy = clampMoodEnergy(log?.metrics?.eveningEnergy ?? log?.metrics?.morningEnergy ?? 5);
  return { mood, energy };
}

function clampMoodEnergy(value: number): number {
  return Math.min(10, Math.max(1, Math.round(value)));
}
